import { createMemo, createSignal, For, Show, type JSX } from "solid-js";
import { Portal } from "solid-js/web";
import MinecraftItem, { cssSize } from "./MinecraftItem";
import { formatMinecraftText } from "./MinecraftText";
import { useCraftingResolver } from "../services/craftingResolverContext";
import { parseSnbt } from "../services/snbtReader";
import { decodePotionDamage, potionEffectKey, potionEffectKeyById, potionLiquidColor } from "../services/potionBitfield";
import { spawnEggColors, SPAWN_EGG_ITEM_ID } from "../services/spawnEggColors";
import { maxDurability } from "../services/itemDurability";
import { getEnchantmentName, formatEnchantmentLevel } from "../services/enchantmentNames";
import { resolveItemDisplayName, stripColorCodes } from "../services/itemDisplayName";
import { cbItemByWireId } from "../services/cbItems";
import { isEmptyStack, type ItemStack, type ItemStackNbt } from "../models/itemStack";
import { showItemInfo, ITEM_DETAILS_FLAG } from "./ItemInfoView";
import MapImage, { MAP_CACHE_PAGE_FLAG } from "./MapImage";
import { isFeatureEnabled } from "../services/featureFlags";
import { useTranslation } from "../hooks/useTranslation";
import styles from "./ItemStackView.module.css";

interface ItemStackViewProps {
    stack: ItemStack;
    size?: number | string;
    bare?: boolean;
    showCount?: boolean;
    showDurability?: boolean;
    titleOverride?: string;
    showTooltip?: boolean;
    loreOverride?: string[];
    inlineTooltip?: boolean;
    // Lines the item itself cannot answer for, a shop's stock and price ladder above all, so a caller
    // that needs them does not reproduce the whole tooltip to get them. Pass a thunk when the caller
    // renders many tiles: Solid materialises JSX as soon as it is called, and a grid of a few thousand
    // items would build every tooltip at load for the one that gets hovered.
    tooltipExtra?: JSX.Element | (() => JSX.Element);
}

const POTION_IDS = new Set([373, 438, 441]);

export default function ItemStackView(props: ItemStackViewProps) {
    const resolver = useCraftingResolver();
    const { t, langCode } = useTranslation();

    const nbt = createMemo<ItemStackNbt>(() => parseSnbt(props.stack.snbt));

    const resolved = createMemo(() => {
        const s = props.stack;
        if (isEmptyStack(s)) return null;
        const cb = cbItemByWireId(s.itemId);
        if (cb) return { def: undefined, name: cb.key, identifier: cb.identifier };
        const r = resolver();
        let def = s.itemId >= 0
            ? (r?.getItemById(s.itemId, s.damage) ?? r?.getItemById(s.itemId, 0))
            : undefined;
        if (!def && r && s.name) {
            const items = r.getAllItems?.() ?? [];
            def = items.find((i) => i.name === s.name && i.metadata === s.damage)
                ?? items.find((i) => i.name === s.name);
        }
        const name = def?.name ?? s.name;
        const identifier = s.identifier ?? (name ? `minecraft:${name}` : undefined);
        return { def, name, identifier };
    });

    const isPotion = () => {
        const s = props.stack;
        return POTION_IDS.has(s.itemId) || resolved()?.name === "potion" || resolved()?.name === "splash_potion" || resolved()?.name === "lingering_potion";
    };

    // Checked ahead of the resolver for the same reason POTION_IDS is: items.json is 1.13-era, where 383
    // is yellow glazed terracotta, so a 1.8 spawn egg resolves to a real but wrong item, not to nothing.
    const isSpawnEgg = () => props.stack.itemId === SPAWN_EGG_ITEM_ID;

    const eggColors = createMemo(() => (isSpawnEgg() ? spawnEggColors(props.stack.damage) : undefined));

    // A potion given by command carries its effects in NBT with the damage value left at water, so the
    // tooltip reads them off whichever of the two the item actually filled in.
    const potionFlags = createMemo(() => {
        if (!isPotion()) return "";
        const decoded = decodePotionDamage(props.stack.damage);
        const splash = decoded.splash ? "splash" : "";
        const custom = nbt().customPotionEffects ?? [];
        if (custom.length > 0) {
            const named = custom.map((effect) =>
                `effect:${potionEffectKeyById(effect.id) ?? effect.id}${effect.amplifier > 0 ? `+${effect.amplifier}` : ""}`);
            return [...named, splash].filter(Boolean).join(" ");
        }
        if (props.stack.damage === 0) return "";
        return [`effect:${potionEffectKey(decoded)}`, decoded.tierII ? "II" : "", decoded.extended ? "ext" : "", splash]
            .filter(Boolean).join(" ");
    });

    const effectiveIdentifier = createMemo(() => {
        if (isSpawnEgg()) return "minecraft:spawn_egg";
        const base = resolved()?.identifier ?? `itemid:${props.stack.itemId}`;
        if (!isPotion()) return base;
        const decoded = decodePotionDamage(props.stack.damage);
        if (resolved()?.name === "potion" && decoded.splash) {
            return "minecraft:splash_potion";
        }
        return base;
    });

    const displayName = createMemo(() => {
        if (props.titleOverride) return props.titleOverride;
        return resolveItemDisplayName(props.stack, resolver());
    });

    const enchanted = createMemo(() => {
        const n = nbt();
        return (n.ench?.length ?? 0) > 0;
    });

    const showDurabilityBar = createMemo(() => {
        if (props.showDurability === false) return false;
        const n = nbt();
        if (n.unbreakable) return false;
        if (props.stack.damage <= 0) return false;
        if (isPotion()) return false;
        const name = resolved()?.name;
        return maxDurability(name) !== undefined;
    });

    const durabilityPct = createMemo(() => {
        const name = resolved()?.name;
        const max = maxDurability(name);
        if (!max) return 1;
        return Math.max(0, Math.min(1, 1 - props.stack.damage / max));
    });

    const effectiveItemId = createMemo(() => {
        const resolvedId = resolved()?.def?.id;
        if (typeof resolvedId === "number" && resolvedId >= 0) return resolvedId;
        if (props.stack.itemId >= 0) return props.stack.itemId;
        return undefined;
    });

    const countVisible = createMemo(() => {
        if (props.showCount === false) return false;
        if (props.showCount === true) return props.stack.count > 0;
        return props.stack.count > 1;
    });

    const [tooltipPos, setTooltipPos] = createSignal<{ x: number; y: number } | null>(null);
    const onMouseEnter = (e: MouseEvent) => {
        if (!props.showTooltip) return;
        setTooltipPos({ x: e.clientX, y: e.clientY });
    };
    const onMouseMove = (e: MouseEvent) => {
        if (!props.showTooltip) return;
        if (tooltipPos()) setTooltipPos({ x: e.clientX, y: e.clientY });
    };
    const onMouseLeave = () => setTooltipPos(null);

    const onContextMenu = (e: MouseEvent) => {
        if (!isFeatureEnabled(ITEM_DETAILS_FLAG)) return;
        e.preventDefault();
        setTooltipPos(null);
        showItemInfo({
            itemId: props.stack.itemId,
            count: props.stack.count,
            damage: props.stack.damage,
            identifier: resolved()?.identifier ?? effectiveIdentifier(),
            name: resolved()?.name,
            displayName: displayName(),
            snbt: props.stack.snbt,
        });
    };

    const durabilityMax = () => maxDurability(resolved()?.name);
    const enchantLines = createMemo(() => {
        const list = nbt().ench ?? [];
        return list.map((e) => {
            const name = getEnchantmentName(e.id, langCode());
            const lvl = formatEnchantmentLevel(e.lvl);
            return `§7${name} ${lvl}`;
        });
    });

    const loreLines = () => props.loreOverride ?? nbt().lore ?? [];
    const customName = () => nbt().displayName;

    const resolveTooltipExtra = () =>
        typeof props.tooltipExtra === "function" ? props.tooltipExtra() : props.tooltipExtra;

    const tooltipBody = () => (
        <>
            <div class={`${styles.mcTooltipTitle} ${customName() ? styles.mcTooltipCustom : ''}`}>
                {formatMinecraftText(customName() ?? displayName())}
            </div>

            <For each={enchantLines()}>
                {(line) => <div class={styles.mcTooltipLine}>{formatMinecraftText(line)}</div>}
            </For>

            <For each={loreLines()}>
                {(line) => <div class={styles.mcTooltipLine}>{formatMinecraftText(line)}</div>}
            </For>

            <Show when={nbt().map3 && isFeatureEnabled(MAP_CACHE_PAGE_FLAG)}>
                <div class={styles.mcTooltipMap}>
                    <MapImage map3={nbt().map3!} alt={stripColorCodes(customName() ?? displayName())} size={128} hideWhenMissing />
                </div>
            </Show>

            <Show when={showDurabilityBar() && durabilityMax()}>
                <div class={styles.mcTooltipMeta}>
                    {formatMinecraftText(
                        `§fDurability: ${Math.max(0, durabilityMax()! - props.stack.damage)} / ${durabilityMax()}`
                    )}
                </div>
            </Show>

            <Show when={(nbt().repairCost ?? 0) > 0}>
                <div class={styles.mcTooltipLine}>&nbsp;</div>
                <div class={styles.mcTooltipMeta}>{formatMinecraftText(`§7${t('ITEM_TOOLTIP_REPAIR_VALUE')}: ${nbt().repairCost}`)}</div>
            </Show>

            <Show when={nbt().unbreakable}>
                <div class={styles.mcTooltipMeta}>{formatMinecraftText(`§b${t('ITEM_TOOLTIP_UNBREAKABLE')}`)}</div>
            </Show>

            <Show when={nbt().tagCount}>
                <div class={styles.mcTooltipMeta}>{formatMinecraftText(`§7${t('ITEM_TOOLTIP_NBT_TAGS', nbt().tagCount!)}`)}</div>
            </Show>

            <Show when={props.stack.count > 1}>
                <div class={styles.mcTooltipMeta}>{formatMinecraftText(`§7x${props.stack.count}`)}</div>
            </Show>

            <div class={styles.mcTooltipId}>
                {resolved()?.identifier ?? `itemid:${props.stack.itemId}`}
                <Show when={props.stack.damage > 0 && !isPotion()}>
                    <span class={styles.mcTooltipDamage}>:{props.stack.damage}</span>
                </Show>
            </div>

            <Show when={potionFlags()}>
                <div class={styles.mcTooltipPotion}>{potionFlags()}</div>
            </Show>

            <Show when={props.tooltipExtra}>
                <div class={styles.mcTooltipExtra}>{resolveTooltipExtra()}</div>
            </Show>
        </>
    );

    // The wrapper already applies a relative size, and passing it on would apply it a second time.
    const iconSize = () => (typeof props.size === "string" ? "100%" : props.size);

    const icon = () => (
        <div
            class={styles.wrap}
            style={props.size ? { width: cssSize(props.size), height: cssSize(props.size) } : undefined}
            onMouseEnter={onMouseEnter}
            onMouseMove={onMouseMove}
            onMouseLeave={onMouseLeave}
            onContextMenu={onContextMenu}
        >
            <MinecraftItem
                itemId={effectiveItemId()}
                itemIdentifier={effectiveIdentifier()}
                metadata={isPotion() || isSpawnEgg() ? 0 : props.stack.damage}
                title={props.showTooltip || props.inlineTooltip ? undefined : displayName()}
                size={iconSize()}
                bare={props.bare}
                enchanted={enchanted()}
                renderAsHead={!!nbt().skullOwner}
                skullOwner={nbt().skullOwner}
                potionColor={isPotion() ? potionLiquidColor(decodePotionDamage(props.stack.damage), nbt().customPotionEffects) : undefined}
                spawnEggColors={eggColors()}
            />
            <Show when={countVisible()}>
                <span class={styles.count}>{props.stack.count}</span>
            </Show>
            <Show when={showDurabilityBar()}>
                <div class={styles.damageBar}>
                    <span style={{ width: `${Math.round(durabilityPct() * 100)}%` }} />
                </div>
            </Show>
        </div>
    );

    return (
        <Show when={!isEmptyStack(props.stack)}>
            <Show when={props.inlineTooltip} fallback={icon()}>
                <div class={styles.inlineWrap}>
                    {icon()}
                    <div class={`${styles.mcTooltip} ${styles.mcTooltipInline}`}>{tooltipBody()}</div>
                </div>
            </Show>

            <Show when={props.showTooltip && tooltipPos()}>
                {(pos) => (
                    <Portal mount={document.body}>
                        <div
                            class={styles.mcTooltip}
                            style={{
                                left: `${clampTooltipX(pos().x + 12)}px`,
                                top: `${clampTooltipY(pos().y + 12)}px`,
                            }}
                        >
                            {tooltipBody()}
                        </div>
                    </Portal>
                )}
            </Show>
        </Show>
    );
}

function clampTooltipX(x: number): number {
    const maxW = 360;
    return Math.min(x, window.innerWidth - maxW - 8);
}

function clampTooltipY(y: number): number {
    const estH = 200;
    return Math.min(y, window.innerHeight - estH - 8);
}

